<template>
  <div>
    <p class="text-center text--secondary mb-6" style="font-size: 15px; line-height: 1.6;">
      {{ $t('2fa.method.totp.register.subtitle') }}
    </p>

    <v-alert v-if="error" type="error" dense text dismissible @input="error = null" class="mb-4">
      {{ error }}
    </v-alert>

    <!-- QR Code Section -->
    <div v-if="instructionsData?.totp_qrcode" class="text-center mb-6">
      <v-card outlined class="d-inline-block pa-4" style="border-radius: 12px;">
        <pling-qr-code
            :qr_code_url="instructionsData?.totp_qrcode"
            :width="220"
        />
      </v-card>
      <p class="text-caption text--secondary mt-3">
        {{ $t('2fa.method.totp.register.scan_instruction') }}
      </p>
    </div>

    <!-- Manual Entry Section -->
    <div v-if="instructionsData?.totp_secret" class="mb-6">
      <p class="text-caption text--secondary text-center mb-2">
        {{ $t('2fa.method.totp.register.manual_entry_label') }}
      </p>
      <v-card outlined class="pa-3" style="background-color: #f5f5f5;">
        <div class="d-flex align-center">
          <code class="flex-grow-1 secret-code" style="background-color: transparent">
            {{ instructionsData?.totp_secret }}
          </code>
          <v-btn
              icon
              small
              @click="copySecret"
              class="ml-2 flex-shrink-0"
              :color="copied ? 'success' : 'grey'"
          >
            <v-icon small>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
          </v-btn>
        </div>
      </v-card>
    </div>

    <!-- Verification Code Input -->
    <v-text-field
        v-model="code"
        :label="$t('2fa.method.totp.register.code_label')"
        outlined
        dense
        hide-details="auto"
        :maxlength="codeLength"
        :counter="codeLength"
        :rules="[rules.required, rules.numeric, rules.length(codeLength)]"
        :loading="twoFactorAuth.verificationLoading"
        :disabled="twoFactorAuth.verificationLoading"
        @keydown.enter="verify"
        autofocus
        autocomplete="one-time-code"
        inputmode="numeric"
        pattern="[0-9]*"
        class="mb-5"
    ></v-text-field>

    <v-btn
        color="primary"
        depressed
        block
        large
        @click="verify"
        :loading="twoFactorAuth.verificationLoading"
        :disabled="!isCodeValid"
        class="text-none mb-4"
        style="font-weight: 500; letter-spacing: 0;"
    >
      {{ $t('2fa.method.totp.register.verify_button') }}
    </v-btn>

    <v-divider class="my-4"></v-divider>

    <div class="d-flex justify-center" v-if="canCancel">
      <v-btn
          text
          small
          @click="cancel"
          :disabled="twoFactorAuth.verificationLoading"
          class="text-none"
          color="grey darken-1"
      >
        {{ $t('buttons.cancel') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import PlingQrCode from '@/components/elements/plingQrCode.vue';
import {twoFactorAuthStore} from "@/store/pinia/twoFactorAuthStore";

export default {
  name: 'TotpMethodInstructions',
  components: {PlingQrCode},
  props: {
    instructionsData: {
      type: Object,
      required: false,
      default: null
    },
    canCancel: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const twoFactorAuth = twoFactorAuthStore();
    return {twoFactorAuth};
  },

  mounted() {

    this.$emit('title-change', this.$t('2fa.method.totp.register.title'))


  },
  data() {
    return {
      code: '',
      error: null,
      copied: false,
      rules: {
        required: value => !!value || this.$t('validation.required'),
        numeric: value => /^\d+$/.test(value) || this.$t('validation.numeric'),
        length: len => value => (value && value.length === len) || this.$t('validation.code_length', {length: len}),
      },
    };
  },
  computed: {
    codeLength() {
      return 6;
    },
    isCodeValid() {
      return /^\d{6}$/.test(this.code);
    }
  },
  methods: {
    async verify() {
      if (!this.isCodeValid) return;
      this.error = null;

      try {
        const payload = await this.twoFactorAuth.checkRegister2FAMethodCode(this.code);

        if (payload.data.payload) {
          this.$emit('completed', payload);
          this.code = '';
          return;
        }
        this.code = '';
        this.error = this.$t('2fa.method.totp.register.error.verification_failed');

      } catch (e) {
        this.error = e.response?.data?.message || this.$t('2fa.method.totp.register.error.verification_error');
      }
    },

    async copySecret() {
      try {
        await navigator.clipboard.writeText(this.instructionsData?.totp_secret);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy secret:', err);
      }
    },

    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(0, 0, 0, 0.12);
}

::v-deep .v-counter {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.secret-code {
  color: #333;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  word-break: break-word;
  line-height: 1.5;
  letter-spacing: 1px;
}
</style>
